/**
 * DeclareWinnerModal — confirms and collects the winning variant for the
 * dedicated `status: 'decided'` transition (AddEditExperimentV2's
 * "Declare winner" action, not the status dropdown).
 *
 * Declaring a winner is a one-way door on the server: variants, weights,
 * rollout and platform all lock once decided, and winning_variant itself
 * can never change again. This dialog exists specifically to make that
 * consequence visible before the operator commits — the status `<Select>`
 * intentionally cannot reach `decided` on its own.
 *
 *   <DeclareWinnerModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     variants={serverExperiment?.variants}
 *     onConfirm={(variantName) => dispatch(declareExperimentWinner(...))}
 *   />
 *
 * `variants` MUST come from the loaded server copy of the experiment, not
 * from live form state: the server validates `winning_variant` against
 * the STORED variants, so an unsaved rename in the form would offer a
 * name the PUT is guaranteed to reject.
 *
 * `onConfirm` may resolve `{ ok: false, message }` to report a failure.
 * Anything else — including `undefined` — counts as success. On failure
 * the dialog stays open with the operator's selection intact and shows
 * `message` inline; only a success closes and resets it.
 */
import React, { useRef, useState } from 'react';
import Modal from '../../redesign/Modal';
import { Button } from '../../redesign';
import { Select } from '../../redesign/edit/TextInput';

const GENERIC_ERROR = 'Could not declare the winner. Please try again.';

export default function DeclareWinnerModal({ open, onClose, variants, onConfirm }) {
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Monotonic token identifying the current attempt. Closing the dialog
  // (or starting another attempt) bumps it, which turns any earlier
  // in-flight result into a no-op — see handleClose.
  const attemptRef = useRef(0);

  const reset = () => {
    setBusy(false);
    setSelected('');
    setError('');
  };

  // The escape hatch. `apiV2` sets no timeout, so a hung PUT would
  // otherwise trap the operator in a dialog whose every exit is disabled
  // until a page reload. Closing is therefore always allowed: the
  // in-flight request is simply abandoned — bumping the attempt token
  // means its eventual result can neither reopen nor mutate this dialog.
  const handleClose = () => {
    attemptRef.current += 1;
    reset();
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!selected || busy) return;
    const attempt = (attemptRef.current += 1);
    setBusy(true);
    setError('');

    let result;
    try {
      result = await onConfirm?.(selected);
    } catch (e) {
      // A synchronous throw out of onConfirm must not leave `busy` stuck
      // on — that is the same dead end as a hung request.
      result = { ok: false, message: e?.message };
    }

    // Abandoned (closed mid-flight) or superseded — leave state alone.
    if (attemptRef.current !== attempt) return;

    if (result && result.ok === false) {
      // Keep `selected`: the operator should not have to re-pick the arm
      // they already chose just to retry.
      setBusy(false);
      setError(result.message || GENERIC_ERROR);
      return;
    }

    reset();
    onClose?.();
  };

  const variantNames = (variants || [])
    .map((v) => (v.name || '').trim())
    .filter(Boolean);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      eyebrow="Experiments"
      title="Declare winner"
      maxWidth={480}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!selected || busy}>
            {busy ? 'Declaring…' : 'Declare winner'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="text-text-muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
          New devices will get this arm at 100%. Variants, weights and rollout lock once decided.
        </p>
        <Select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={busy}
          aria-label="Winning variant"
        >
          <option value="">Choose the winning variant…</option>
          {variantNames.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </Select>
        {error && (
          <p
            role="alert"
            className="bg-coral-soft text-coral-dark"
            style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5, borderRadius: 8, padding: '8px 10px' }}
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
